import { useEffect, useState } from "react";
import { WithdrawTypeListResponseList } from "../models/response/withdrawTypeListResponse";
import { Button, Card, CardBody, NumberInput } from "../../../components/ui";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import withdrawService from "../services/withdraw.service";
import localization from "../../../config/localization";
import homeService from "../../home/services/home.service";
import { GetBalanceResponse } from "../../home/models/response/getBalanceResponse";
import { WithdrawFeesTypeListResponseList, UserBankDetailsList, BonusDetail } from "../models";
import { Dropdown } from "../../../components/ui/inputs";
import { ConfirmationModal } from "../../../components/ui/modals/ConfirmationModal";
import { formatUtcToLocalDateTime } from "../../../utils/dateTimeHelper";
import { toastService as toast } from "../../../components/ui/toast/toast.service";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import { DeviceType } from "../../../models/constants/deviceType";
import userDeviceStore from "../../../store/device.store";
import { userPendingStore } from "../../users/store/userPendingData.store";

interface CreateWithdrawRequestProps {
  selectedWithdrawType: WithdrawTypeListResponseList;
  withdrawFlow: string;
  username: string | null;
  onBack: () => void;
}

export default function CreateWithdrawRequest({
  selectedWithdrawType,
  withdrawFlow,
  username,
  onBack,
}: CreateWithdrawRequestProps) {
  const [amount, setAmount] = useState<string>("");
  const [amountError, setAmountError] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [userBalance, setUserBalance] = useState<GetBalanceResponse | null>(null);
  const [userBankDetailsList, setUserBankDetailsList] = useState<UserBankDetailsList[]>([]);
  const [withdrawFeesTypesList, setWithdrawFeesTypesList] = useState<WithdrawFeesTypeListResponseList[]>([]);
  const [selectedFeeType, setSelectedFeeType] = useState<WithdrawFeesTypeListResponseList | null>(null);
  const [selectedUserBank, setSelectedUserBank] = useState<UserBankDetailsList | null>(null);
  const [feeAmount, setFeeAmount] = useState<number>(0);
  const [receivedAmount, setReceivedAmount] = useState<number>(0);
  const [removedFreeSpinBonusIdList, setRemovedFreeSpinBonusIdList] = useState<string[]>([]);
  const [showActiveBonusCancelModal, setShowActiveBonusCancelModal] = useState<boolean>(false);
  const [activeBonusList, setActiveBonusList] = useState<string[]>([]);
  const [nextclick, setNextclick] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const deviceType = userDeviceStore((state) => state.deviceType);
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);

  useEffect(() => {
    dispatch(changeLoaderState(true));
    init();
  }, []);

  const init = async () => {
    const balanceResponse = await homeService.fetchUserBalance();
    if (balanceResponse.isSuccess && balanceResponse.data) {
      setUserBalance(balanceResponse.data);
    }
    const banks = await withdrawService.getUserBankDetailsList();
    if (banks && banks.length > 0) {
      setUserBankDetailsList(banks);
    }
    const fees = await withdrawService.getWithdrawFeesTypesList();
    if (fees && fees.length > 0) {
      setWithdrawFeesTypesList(fees);
      setSelectedFeeType(fees[0]);
    }
    dispatch(changeLoaderState(false));
  };

  const HandleFeeTypeSelect = (feeType: WithdrawFeesTypeListResponseList) => {
    if (Number(amount) > 0) {
      const num = Number(amount);
      if ((feeType?.usersfreecount ?? 0) <= 0) {
        if (feeType?.amounttype === "Fix Amount") {
          if (num - feeType.fee < 0) {
            setAmountError(localization.amount_less_than_fee_amount);
            return;
          }
          setFeeAmount(feeType.fee)
          setReceivedAmount(num - feeType.fee)
        }
        else {
          const feeAmount = Math.floor((num * (feeType?.fee ?? 0) / 100))
          if (feeAmount < 0) {
            setAmountError(localization.amount_less_than_fee_amount);
            return;
          }
          setFeeAmount(feeAmount)
          setReceivedAmount(num - feeAmount)
        }
      }
      else {
        setFeeAmount(0)
        setReceivedAmount(num)
      }
    }
    setSelectedFeeType(feeType);
  };

  const handleAccountChange = (accountNumber: string) => {
    const selectedBank = userBankDetailsList.find((bank) => bank.accountnumber === accountNumber);
    if (selectedBank) {
      setSelectedUserBank(selectedBank);
      setAccountError(null);
    }
  };

  const handleAmountChange = (value: string) => {
    const num = Number(value);

    if (num === 0) {
      setAmountError(null);
      return;
    }

    if (isNaN(num)) {
      setAmountError(localization.enter_valid_amount);
      return;
    }

    if (num > (userBalance?.withdrawBalance ?? 0)) {
      setAmountError(localization.amount_less_than_balance);
      return;
    }

    if (
      num < selectedWithdrawType?.minimumAmount ||
      num > selectedWithdrawType?.maximumAmount
    ) {
      setAmountError(
        `${localization.amount_between} \u20B9${selectedWithdrawType?.minimumAmount} ${localization.and} \u20B9${selectedWithdrawType?.maximumAmount}`
      );
      return;
    }

    if ((selectedFeeType?.usersfreecount ?? 0) <= 0) {
      if (selectedFeeType?.amounttype === "Fix Amount") {
        if (num - selectedFeeType.fee < 0) {
          setAmountError(localization.amount_less_than_fee_amount);
          return;
        }
        setFeeAmount(selectedFeeType.fee)
        setReceivedAmount(num - selectedFeeType.fee)
      }
      else {
        const feeAmount = Math.floor((num * (selectedFeeType?.fee ?? 0) / 100))
        if (feeAmount < 0) {
          setAmountError(localization.amount_less_than_fee_amount);
          return;
        }
        setFeeAmount(feeAmount)
        setReceivedAmount(num - feeAmount)
      }
    }

    setAmount(value);
    setAmountError(null);
  };

  const handleNextClick = async () => {
    if (nextclick) {
      return;
    }

    try {
      setNextclick(true);

      if (!amount) {
        setAmountError(localization.valid_enter_amount);
        return;
      }

      if (!selectedUserBank) {
        setAccountError(localization.select_userbank);
        return;
      }

      const activeBonusCount = await withdrawService.getUserActiveBonusCount();
      if (activeBonusCount && activeBonusCount.activebonuscount > 0 && activeBonusCount.activebonusnames !== null && activeBonusCount.bonusdetails !== null) {

        const bonusDetailsArray: BonusDetail[] = Array.isArray(activeBonusCount.bonusdetails)
          ? activeBonusCount.bonusdetails
          : activeBonusCount.bonusdetails
            ? (JSON.parse(activeBonusCount.bonusdetails) as BonusDetail[])
            : [];

        const formattedBonusList = bonusDetailsArray
          .sort(
            (a, b) =>
              Number(!!a.startDate && !!a.endDate) -
              Number(!!b.startDate && !!b.endDate)
          )
          .map((bonus) => {
            if (bonus.startDate && bonus.endDate) {
              return `${bonus.name} (From ${formatUtcToLocalDateTime(
                bonus.startDate,
                false
              )} until ${formatUtcToLocalDateTime(
                bonus.endDate,
                false
              )})`;
            }
            return bonus.name;
          });

        setActiveBonusList(formattedBonusList ?? []);

        if (activeBonusCount.freespinbonuscount > 0) {
          const freeSpinBonusDetails = await withdrawService.getUserActiveFreeSpinBonusDetails();
          if (freeSpinBonusDetails && freeSpinBonusDetails.length > 0) {
            const itemsToRemove: typeof freeSpinBonusDetails = [];
            for (const bonusDetail of freeSpinBonusDetails) {
              if (bonusDetail.qtechBonusId) {
                const request = {
                  bonusId: bonusDetail.qtechBonusId,
                  playerId: bonusDetail.providerUserId,
                };
                const freeSpinBonusHistory =
                  await withdrawService.getFreeSpinBonusHistoryFromProvider(request);

                if (freeSpinBonusHistory && freeSpinBonusHistory.status.toLowerCase() !== "promoted") {
                  itemsToRemove.push(bonusDetail);
                }
              }
            }
            freeSpinBonusDetails.filter(
              (item) => !itemsToRemove.includes(item)
            );

            setRemovedFreeSpinBonusIdList(itemsToRemove.map((item) => item.qtechBonusId));

            if (freeSpinBonusDetails.length > 0) {
              setShowActiveBonusCancelModal(true);
            }
            else if (activeBonusCount.activebonuscount - activeBonusCount.freespinbonuscount > 0) {
              setShowActiveBonusCancelModal(true);
            }
            else {
              await handleWithdrawSubmit();
            }
          }
          else {
            setShowActiveBonusCancelModal(true);
          }
        }
        else {
          setShowActiveBonusCancelModal(true);
        }
      }
      else {
        await handleWithdrawSubmit();
      }
    }
    finally {
      setNextclick(false);
    }
  };

  const handleWithdrawSubmit = async () => {
    const response = await withdrawService.withdrawSubmit(
      selectedWithdrawType,
      Number(amount),
      receivedAmount,
      feeAmount,
      selectedUserBank,
      selectedFeeType,
      username,
      showActiveBonusCancelModal,
      removedFreeSpinBonusIdList,
      withdrawFlow,
      accessModeSource.sourceType
    );
    if (response.success) {
      userPendingStore.getState().fetchPendingData();
      navigate("/withdraw-under-process");
    }
    else {
      toast.error(response.message);
    }
  };

  const label = (
    <span className="flex flex-wrap items-center gap-1 mb-1">
      <span className="text-[#A9A9A9] font-medium text-base mr-1">
        {localization.amount}
      </span>

      <span className="text-[#A9A9A9] text-xs flex items-center gap-1 flex-wrap mb-1">
        <span className="text-[#A9A9A9] text-xs font-medium">
          ({localization.min_text} {"\u20B9"}{selectedWithdrawType?.minimumAmount?.toFixed(2)} -
          {localization.max_text} {"\u20B9"}{selectedWithdrawType?.maximumAmount?.toFixed(2)})
        </span>
      </span>
    </span>
  );

  const activeBonusDescription = (
    <ul className="mt-2 list-disc pl-5 text-left text-sm text-[#111111]">
      {activeBonusList.map((b, i) => (
        <li key={i}>{b}</li>
      ))}
    </ul>
  );

  const content = (
    <div className="p-4">

      <div className="w-full flex justify-end items-center mb-3">
        <div className="flex items-center bg-[#1A1A1A] border border-[#2E2E2E] rounded-[6px] px-2 py-1 gap-2">
          <img
            src={
              selectedWithdrawType?.logoUrl &&
              `${BLOB_IMAGE_URL}${BlobContainerConsts.WithdrawType}/${selectedWithdrawType?.logoUrl}`
            }
            alt={selectedWithdrawType?.name}
            className="w-6 h-6 object-contain"
          />
          <p className="text-sm font-bold text-white">
            {selectedWithdrawType?.accountName}
          </p>
          <Button
            onClick={onBack}
            className="text-xs font-medium bg-[#460413] text-[#FFBDCC] px-2 py-2 rounded-full hover:bg-[#8a0024] transition"
          >
            {localization.change_btn}
          </Button>
        </div>
      </div>

      <div className="mt-10">
        <span className="font-normal mr-1 text-[#DBDBDB] text-base">{localization.Widthdrawable_Bal}</span>
        <span className="text-white font-semibold text-base">{"\u20B9"}{(userBalance?.withdrawBalance ?? 0).toFixed(2)}</span>
      </div>

      <div className="mt-8">
        <NumberInput
          label={label}
          placeholder={localization.enter_amount}
          error={amountError}
          value={amount}
          maxLength={8}
          onChange={(e) => setAmount(e.target.value)}
          min={selectedWithdrawType?.minimumAmount}
          max={selectedWithdrawType?.maximumAmount}
          onInput={(e) => handleAmountChange(e.currentTarget.value)}
          onCopy={(e) => e.preventDefault()}
          onPaste={(e) => e.preventDefault()}
        />
      </div>

      {feeAmount > 0 && receivedAmount > 0 && (
        <div className="mt-3">
          <p className="text-[#C7C7CC] text-sm font-normal">
            <span>{localization.fee}</span>
            <span className="ml-1">{"\u20B9"}{feeAmount.toFixed(2)}</span>
            <span className="mx-2">{"-"}</span>
            <span>{localization.fees_info}</span>
            <span className="ml-1">{"\u20B9"}{receivedAmount.toFixed(2)}</span>
          </p>
        </div>
      )}

      <div className="mt-10">
        <p className="text-white text-base font-medium mb-4">
          {localization.select_category_lbl}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {withdrawFeesTypesList.map((item) => {
          const isSelected = item.id === selectedFeeType?.id;

          return (
            <div
              key={item.id}
              onClick={() => HandleFeeTypeSelect(item)}
              className={`relative cursor-pointer rounded-[4px] border ${isSelected ? "border-[#257767]" : "border-[#353535]"}`}
            >
              <div className="absolute top-0 right-0">
                <div className="rounded-tr-[3px] rounded-bl-[6px] border-l border-b border-[#2E2E2E] bg-[#1A1A1A] px-2 py-1.5 text-[10px] font-semibold text-[#D1D1D1]">
                  {item.description}
                </div>
              </div>

              <div className="flex items-center justify-center gap-3 mt-8 px-4">
                <div className="w-14 h-14 rounded-[10px] bg-[#181818] flex items-center justify-center">
                  <img
                    src={
                      item.icon
                        ? `${BLOB_IMAGE_URL}${BlobContainerConsts.WithdrawFee}/${item.icon}`
                        : "/img/icons/default-fee.png"
                    }
                    alt={item.displayname}
                    className="w-8 h-8 object-contain"
                  />
                </div>

                <span className="text-[#F5F5F5] font-medium text-base">
                  {item.displayname}
                </span>
              </div>

              <div className="my-3 border-t border-[#2B2B2B]" />

              <p
                className={`text-center text-sm pb-3 font-semibold uppercase text-[#C7C7CC]`}>
                {localization.fee}
                <span
                  className={`ml-2 ${item.usersfreecount > 0
                    ? "text-[#47D7A3]"
                    : item.amounttype === "Fix Amount"
                      ? "text-[#EB7C7E]"
                      : "text-[#EB7C7E]"
                    }`}
                >
                  {item.usersfreecount > 0 ? (
                    <>
                      {"\u20B9"}00.00
                    </>
                  ) : item.amounttype === "Fix Amount" ? (
                    <>
                      {"\u20B9"}
                      {item.fee.toFixed(2)}
                    </>
                  ) : (
                    <>
                      {item.fee.toFixed(2)}{"\u0025"}
                    </>
                  )}
                </span>
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        <Dropdown
          label={localization.select_account_to_pay}
          options={userBankDetailsList?.map((bank) => ({
            label: bank.accountnumber,
            value: bank.accountnumber,
          })) ?? []
          }
          placeholder={localization.select_account_to_pay}
          value={selectedUserBank?.accountnumber || undefined}
          onChange={(value) => handleAccountChange(value)}
          error={accountError}
        />
      </div>

      <Button
        type="button"
        className={`mt-8 w-full rounded-lg py-4 text-white bg-primary-gradient font-semibold hover:opacity-90 transition-all duration-200 ${nextclick ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={handleNextClick}
        disabled={nextclick}
      >
        {nextclick ? localization.processing : localization.next}
      </Button>

      {showActiveBonusCancelModal && (
        <ConfirmationModal
          isOpen={showActiveBonusCancelModal}
          onConfirm={() => {
            handleWithdrawSubmit();
          }}
          onCancel={() => {
            setShowActiveBonusCancelModal(false);
          }}
          description={activeBonusDescription}
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          title={localization.confirm_withdraw}
          confirmText={localization.confirm_btn}
          cancelText={localization.cancel_btn}
        />
      )}
    </div >
  );

  return deviceType !== DeviceType.Web ? (
    content
  ) : (
    <Card>
      <CardBody showGradient>
        <div className="w-full max-w-4xl mx-auto">
          {content}
        </div>
      </CardBody>
    </Card>
  );
}
